import React, { PureComponent } from 'react';
import { browserHistory } from 'react-router';
import { connect } from 'react-redux';
import { Bar, Pie, Line } from 'react-chartjs-2';
import DatePicker from 'react-datepicker';
import Moment from 'moment';
import { Row, Col, OverlayTrigger, Tooltip, Panel, DropdownButton, Glyphicon, MenuItem } from 'react-bootstrap';
import _ from 'lodash';
import { userGetReports } from '../actions/userActions';
import LoadingProgressBar from './common/LoadingProgressBar';
import { caseSetServerError, casesFilterAction, casesRefreshNeededAction } from './../actions/caseActions';
import { numberWithCommas, getFullName, navigateToCase, downloadCsv, filteredRoute, getDateTimeFromUtcInLocalTime } from './../helpers/utils';
import { colors, colorMap } from './../helpers/colors';
import Table from './common/Table';
import Button from './common/Button';


if (typeof window !== 'undefined') {
    import('chartjs-plugin-zoom');
}

class Reports extends PureComponent {
    constructor(props) {
        super(props);
        this.handleDateChange = this.handleDateChange.bind(this);
        this.state = {
            graphs: undefined,
            charges: [],
            startDate: undefined,
            endDate: undefined,
            startDateText: undefined,
            endDateText: undefined,
            loading: false,
        };
    }

    componentDidMount() {
        if (this.props.loggedIn) {
            this.handleDateChange({ startDate: this.props.loggedIn['created_at'] });
        }
    }

    componentWillReceiveProps(nextProps) {
        if (!this.props.loggedIn && nextProps.loggedIn) {
            this.handleDateChange({ startDate: nextProps.loggedIn['created_at'] });
        }
    }

    handleDateChange({ startDate, endDate }) {
        this.setState({ loading: true });
        startDate = startDate || this.state.startDate || (this.props.loggedIn && this.props.loggedIn['created_at']);
        endDate = endDate || this.state.endDate;
        startDate = Moment(startDate).startOf('day');
        if (endDate) {
            endDate = Moment(endDate).endOf('day');
        } else {
            endDate = Moment(endDate);
        }

        if (startDate && endDate && startDate.isAfter(endDate)) {
            endDate = startDate;
        }

        const startDateText = startDate.format(process.env.DATE_FORMAT);
        const endDateText = endDate.format(process.env.DATE_FORMAT);
        startDate = startDate.toDate();
        endDate = endDate.toDate();
        const range = { startDate, endDate, startDateText, endDateText };
        this.setState(range, () => {
            this.props.dispatch(userGetReports(range))
                .then((result) => {
                    this.setState({ loading: false });
                    const data = result && result.data;
                    if (!data || !data.report || !data.range) {
                        const error = "Invalid report request!";
                        console.error(new Error().stack, error);
                        this.props.dispatch(caseSetServerError(error));
                        return;
                    }

                    if ((this.state.startDate && this.state.startDate.toISOString()) === data.range.startDate &&
                        (this.state.endDate && this.state.endDate.toISOString()) === data.range.endDate) {

                        const chargesInfo = data.report.chargesInfo && _.keyBy(data.report.chargesInfo, v => v.id);
                        const charges = (data.report.charges && data.report.charges.map(v => {
                            const description = (v['EventDescription'] || v['description']);
                            const caseId = v['$CaseId$'] || v['caseId'];
                            const info = (chargesInfo && chargesInfo[caseId]) || {}
                            return {
                                charges: {
                                    'DateTime' : getDateTimeFromUtcInLocalTime(v['EventDate'] || v['updated_at'], this.props.loggedIn),
                                    'ClientName' : v['clientName'] || (v['matter'] && v['matter']['client'] && getFullName(v['matter']['client'])),
                                    'CaseType' : info['case_type'],
                                    'Amount' : parseInt(v['amount'], 10) / 100.00,
                                    'Description' : description,
                                    '$CaseId$': caseId,
                                    '$PaymentService$': v['$PaymentService$'],
                                    '$AccountName$': v['$AccountName$'],
                                }
                            };
                        })) || [];

                        this.setState({ graphs: data.report.graphData, charges });
                    }
                })
                .catch((e) => {
                    console.error(e);
                    this.setState({ loading: false });
                });
        });
    }

    render() {       
        const handleChangeStart = startDate => this.handleDateChange({ startDate });
        const handleChangeEnd = endDate => this.handleDateChange({ endDate });
        const format = 'MM/dd/yyyy'; // different rules than process.env.DATE_FORMAT;

        const chargesHeaders = [
            {
                table: 'charges',
                name: 'DateTime',
                label: 'Date / Time',
                width: 120
            },
            {
                table: 'charges',
                name: 'ClientName',
                label: 'Client Name',
                width: 150
            },
            {
                table: 'charges',
                name: 'CaseType',
                label: 'Case Type',
                width: 150,
            },
            {
                table: 'charges',
                name: 'Amount',
                label: 'Amount',
                width: 60,
            },
            {
                table: 'charges',
                name: '$PaymentService$',
                label: 'Payment Method',
                width: 110,
            },
            {
                table: 'charges',
                name: '$AccountName$',
                label: 'Account Name',
                width: 150,
            },
            {
                table: 'charges',
                name: 'Description',
                label: 'Description',
                width: '*',
            },
        ];

        const charges = this.state.charges;
        const chargesPanelHeader = (
            <div style={{ height: 25 }}>
                <h6 style={{ float: 'left', marginTop: 5 }}>{`Paid Charges (${!charges ? 0 : charges.length})`}</h6>
                <div style={{ float: 'right' }}>
                <DropdownButton title={<Glyphicon glyph="option-vertical" />}
                                noCaret
                                pullRight
                                style={{ boxShadow: 'none', marginTop: -8, marginRight: -15, marginBottom: -1, backgroundColor: 'rgba(0,0,0,0)' }}
                >
                    <MenuItem data-hj-whitelist eventKey="downloadCsv" onClick={() => { downloadCsv(chargesHeaders, charges, 'charges.csv'); }}>Download as CSV</MenuItem>
                </DropdownButton>
                </div>
            </div>);
        return (
            <div className="ct-reports">
            <Row style={{ marginLeft: 15, marginRight: 15 }}>
            <Col xs={12} md={6}>
            <Panel data-hj-whitelist header="Date Range">
                <Col xs={12} md={3}>
                <DatePicker
                        placeholder="Start date"
                        dateFormat={format}
                        selected={this.state.startDate}
                        selectsStart
                        startDate={this.state.startDate}
                        endDate={this.state.endDate}
                        onChange={handleChangeStart}
                        todayButton="Today"
                        showMonthDropdown
                        showYearDropdown
                        maxDate={new Date()}
                        minDate={this.props.loggedIn && this.props.loggedIn['created_at'] ? new Date(this.props.loggedIn['created_at']) : undefined}
                />
                </Col>
                <Col xs={12} md={3}>
                <DatePicker
                    placeholder="End date"
                    dateFormat={format}
                    selected={this.state.endDate}
                    selectsEnd
                    startDate={this.state.startDate}
                    endDate={this.state.endDate}
                    onChange={handleChangeEnd}
                    todayButton="Today"
                    showMonthDropdown
                    showYearDropdown
                    maxDate={new Date()}
                    minDate={this.props.loggedIn && this.props.loggedIn['created_at'] ? new Date(this.props.loggedIn['created_at']) : undefined}
                />
                </Col>
                <Col xs={6} md={6}>
                <Button label='View in Dashboard'
                        style={{ float: 'right' }}
                        onClick={() => {
                            const filterObj = {
                                "query": "",
                                "filters": {
                                  "filters": [
                                    {
                                      "operator": ">=",
                                      "left": "intake.CreatedDate",
                                      "right":  this.state.startDateText,
                                    },
                                    {
                                      "operator": "<=",
                                      "left": "intake.CreatedDate",
                                      "right": this.state.endDateText,
                                    }
                                  ],
                                  "type": "and"
                                },
                                "hideFilterPanel": false
                            };

                            const route = filteredRoute('/case/all', filterObj);
                            this.props.dispatch(casesFilterAction(filterObj));
                            browserHistory.push(route);
                            this.props.dispatch(casesRefreshNeededAction(true));
                        }}       
                /> 
                </Col>
            </Panel>
            </Col>
            </Row>
            {this.state.loading || !this.state.graphs ?
            <Row>
                <Col xs={12}>
                    <LoadingProgressBar loading />
                </Col>
            </Row>

            :

            <Row style={{ marginLeft: 15, marginRight: 15 }}>
            {this.state.graphs.map((graphData) => {
                const options = {
                    pan: {
                        enabled: !__IS_MOBILE__,
                        mode: 'x',
                    },
                    zoom: {
                        enabled: !__IS_MOBILE__, // Without it you cannot thumb scroll over the graphs.
                        mode: 'x',
                    },
                    maintainAspectRatio: false,
                    responsive: true,   
                    // Big accounts have hundreds of bars: skip the animations, they make the page stutter.
                    animation: { duration: 0 },
                    hover: { animationDuration: 0 },
                    responsiveAnimationDuration: 0,
                    tooltips: {
                        callbacks: {
                            label: (tooltipItem, data) => {
                                const dataset = data.datasets[tooltipItem.datasetIndex];
                                const currentLabel = dataset.label;
                                const currentValue = dataset.data[tooltipItem.index];
                                return `${currentLabel}: ${numberWithCommas(currentValue)}`;
                            },
                        },
                    },
                };

                const hasData = graphData.type === 'single_value' || !graphData.data || !graphData.data.datasets ||
                    graphData.data.datasets.some(d => (d.data || []).some(v => Number(v) !== 0));
                if (!hasData) {
                    return (
                        <Col xs={12} md={graphData.size} key={graphData.label}>
                            <Panel data-hj-whitelist header={<h5>{graphData.label}</h5>}>
                                <div className="ct-empty">No data in this range</div>
                            </Panel>
                        </Col>
                    );
                }

                let graph;
                const splitDatasets = [];
                const splitLabels = [];
                let dataset;
                let data;
                let index;
                let date;
                let currentIndex = 0;
                const colorsMap = {};
                let type;
                let label;
                switch (graphData.type) {
                    case 'stacked-bar':
                        options['scales'] = {
                            xAxes: [{ stacked: true }],
                            yAxes: [{ stacked: true }],
                        };

                        graphData = _.cloneDeep(graphData);
                        // Labels look like "Google (Feb 2017)". Spelling and case variants of one source
                        // are merged, the biggest sources keep their own color, the rest become "Other".
                        {
                            const MAX_SERIES = 8;
                            const points = [];
                            const dates = [];
                            const names = {};
                            const totals = {};
                            dataset = graphData.data.datasets[0];
                            for (let i = 0; i < dataset.data.length; i += 1) {
                                label = graphData.data.labels[i];
                                index = label.lastIndexOf('(');
                                date = label.substring(index + 1, label.length - 1);
                                type = label.substring(0, index - 1).trim();
                                const key = type.toLowerCase().replace(/\s+/g, ' ');
                                names[key] = names[key] || type;
                                totals[key] = (totals[key] || 0) + Number(dataset.data[i] || 0);
                                if (!dates.includes(date)) {
                                    dates.push(date);
                                }

                                points.push({ key, date, value: Number(dataset.data[i] || 0) });
                            }

                            const top = Object.keys(totals).sort((x, y) => totals[y] - totals[x]).slice(0, MAX_SERIES);
                            const seriesNames = [...top.map(k => names[k])];
                            if (Object.keys(totals).length > top.length) {
                                seriesNames.push('Other');
                            }

                            seriesNames.forEach((name, i) => {
                                splitDatasets.push({
                                    label: name,
                                    data: dates.map(() => 0),
                                    backgroundColor: colors[i % colors.length],
                                    borderColor: colors[i % colors.length],
                                });
                            });

                            points.forEach((point) => {
                                const name = top.includes(point.key) ? names[point.key] : 'Other';
                                splitDatasets.find(v => v.label === name).data[dates.indexOf(point.date)] += point.value;
                            });

                            splitLabels.push(...dates);
                        }

                        options.legend = { position: 'bottom', labels: { boxWidth: 12, padding: 12 } };
                        graphData.data.datasets = splitDatasets;
                        graphData.data.labels = splitLabels;
                        graph = <Panel data-hj-whitelist header={<h5>{graphData.label}</h5>}><Bar data={graphData.data} options={options} height={300} /></Panel>;
                        break;

                    case 'bar':
                        graphData.data.datasets[0]['backgroundColor'] = '#4a6aa8';
                        graphData.data.datasets[0]['borderColor'] = '#4a6aa8';
                        graph = <Panel data-hj-whitelist header={<h5>{graphData.label}</h5>}><Bar data={graphData.data} options={options} height={300} /></Panel>;
                        break;

                    case 'line':
                        graphData.data.datasets[0]['fill'] = false;
                        graphData.data.datasets[0]['backgroundColor'] = '#4a6aa8';
                        graphData.data.datasets[0]['borderColor'] = '#4a6aa8';
                        graph = <Panel data-hj-whitelist header={<h5>{graphData.label}</h5>}><Line data={graphData.data} options={options} height={300} /></Panel>;
                        break;

                    case 'pie':
                        options.tooltips = {
                            callbacks: {
                                label: (tooltipItem, data) => {
                                    const dataset = data.datasets[tooltipItem.datasetIndex];
                                    const currentLabel = data.labels[tooltipItem.index];
                                    const total = dataset.data.reduce((previousValue, currentValue) => parseInt(previousValue, 10) + parseInt(currentValue, 10));
                                    const currentValue = dataset.data[tooltipItem.index];
                                    const precentage = Math.floor(((currentValue / total) * 100) + 0.5);
                                    return `${currentLabel}: ${currentValue} (${precentage}%)`;
                                },
                            },
                        };

                        graphData.data.datasets[0]['backgroundColor'] = colors;
                        graph = <Panel data-hj-whitelist header={<h5>{graphData.label}</h5>}><Pie data={graphData.data} options={options} height={300} /></Panel>;
                        break;

                    case 'single_value':
                        graph = (
                            <OverlayTrigger placement="bottom"
                                            trigger={['hover', 'focus']}
                                            overlay={<Tooltip id="tooltip">{graphData.data.help}</Tooltip>}
                            >
                                <h5>{graphData.data.label}: {numberWithCommas(graphData.data.value)}</h5>
                            </OverlayTrigger>);

                        break;

                    default:
                        this.props.dispatch(caseSetServerError('Invalid graph type!'));
                        console.error('Invalid graph type!');
                        break;
                }

                return <Col key={graphData.label} xs={12} md={graphData.size}>{graph}</Col>;
            })}
            <Col xs={12} style={{minHeight: 330}}>
            <Panel data-hj-whitelist header={chargesPanelHeader}>
            <Table headers={chargesHeaders}
                  customFieldsMap={
                      {
                        'charges.Amount': {
                            type: 'Cash'
                        },
                        'charges.DateTime': {
                            type: 'DateTimeInput'
                        },
                      }
                  }
                  rows={this.state.charges}
                  filterable
                  defaultPageSize={this.state.charges.length > 5 ? 5 : 0}
                  downloadRow={(rowIndex, charge) => {
                      navigateToCase(this.props.dispatch, charge.charges['$CaseId$']);
                  }}
                  downloadBsStyle="link"
            />
            </Panel>
            </Col>
            </Row>
            }
            </div>
        );
    }
}

const mapStateToProps = (state) => {
    return { loggedIn: state.user.loggedIn };
};

export default connect(mapStateToProps)(Reports);
